import React, { memo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import { Save, X } from "lucide-react";

interface ProjectFormData {
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  url: string;
  github_url: string;
  technologies: string[];
}

interface ProjectFormProps {
  formData: ProjectFormData;
  onFormDataChange: (field: string, value: string | string[]) => void;
  onSave: () => void;
  onCancel: () => void;
  onTechnologyKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRemoveTechnology: (tech: string) => void;
  editingId?: string;
}

export const ProjectForm = memo(({
  formData,
  onFormDataChange,
  onSave,
  onCancel,
  onTechnologyKeyDown,
  onRemoveTechnology,
  editingId
}: ProjectFormProps) => {
  const { t } = useTranslation('ui');
  
  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
      <Input
        placeholder={t('project_form.title_placeholder')}
        value={formData.title}
        onChange={(e) => onFormDataChange('title', e.target.value)}
      />
      <Textarea
        placeholder={t('project_form.description_placeholder')}
        value={formData.description}
        onChange={(e) => onFormDataChange('description', e.target.value)}
      />
      <div className="grid grid-cols-2 gap-4">
        <Input
          type="month"
          placeholder={t('project_form.start_date_placeholder')}
          value={formData.start_date}
          onChange={(e) => onFormDataChange('start_date', e.target.value)}
        />
        <Input
          type="month"
          placeholder={t('project_form.end_date_placeholder')}
          value={formData.end_date}
          onChange={(e) => onFormDataChange('end_date', e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input
          placeholder={t('project_form.url_placeholder')}
          value={formData.url}
          onChange={(e) => onFormDataChange('url', e.target.value)}
        />
        <Input
          placeholder={t('project_form.github_url_placeholder')}
          value={formData.github_url}
          onChange={(e) => onFormDataChange('github_url', e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Input
          placeholder={t('project_form.technologies_placeholder')}
          onKeyDown={onTechnologyKeyDown}
        />
        <div className="flex flex-wrap gap-2">
          {formData.technologies.map((tech) => (
            <Badge key={tech} variant="secondary" className="px-2 py-1">
              {tech}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveTechnology(tech)}
                className="ml-1 h-4 w-4 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={onSave} disabled={!formData.title}>
          <Save className="h-4 w-4 mr-2" />
          {t('project_form.save')}
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
        >
          <X className="h-4 w-4 mr-2" />
          {t('project_form.cancel')}
        </Button>
      </div>
    </div>
  );
});

ProjectForm.displayName = "ProjectForm";